// Lane packing for multi-day calendar chips.
//
// Given a set of chips with [startCol, endCol] (inclusive day indices
// into the visible grid row), assign each chip to the lowest lane
// index such that no chip already in that lane overlaps it. Greedy
// interval-scheduling: O(n^2) in the worst case but n is "chips on
// one crew per visible week" which is small (single digits).
//
// Used by CalendarWeekView to vertically stack span chips when two
// install ranges on the same crew overlap. The output `lane` plus the
// chip's startCol/endCol drives CSS grid placement (gridRow = lane+1,
// gridColumn = startCol+2 / span endCol-startCol+1).

export interface LaneInput {
  /** Inclusive day index, 0..N-1. */
  startCol: number;
  /** Inclusive day index, 0..N-1. */
  endCol: number;
}

export interface LaneOutput {
  lane: number;
}

export function packLanes<T extends LaneInput>(items: T[]): Array<T & LaneOutput> {
  // Stable order: by startCol asc, then endCol asc (shorter first within
  // the same start). Predictable packing makes the visual result stable
  // across re-renders when the underlying assignment list reorders.
  const sorted = [...items].sort((a, b) => {
    if (a.startCol !== b.startCol) return a.startCol - b.startCol;
    return a.endCol - b.endCol;
  });

  // lanes[i] is the rightmost endCol currently occupying lane i.
  const laneEnds: number[] = [];
  const result: Array<T & LaneOutput> = [];

  for (const item of sorted) {
    let assigned = -1;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i]! < item.startCol) {
        assigned = i;
        break;
      }
    }
    if (assigned === -1) {
      assigned = laneEnds.length;
      laneEnds.push(item.endCol);
    } else {
      laneEnds[assigned] = item.endCol;
    }
    result.push({ ...item, lane: assigned });
  }

  return result;
}
